import axios from 'axios';
import { cachetime } from '../config';
import { distanceFromSightToPoi } from '@/utils/map';
import { sameCoordGrouper, recalculateCoord } from '@/utils/relocateSameCoordPois';
import poi from '../modules/poi';

export const getSightDistance = ({ coordinate=[] } = {}, target=[0, 0]) => {
  let sightCoordinates = [0, 0];

  if (coordinate[0]) {
    sightCoordinates = Array.isArray(coordinate[0])
      ? coordinate[
        coordinate
          .map((c, idx) => [getSightDistance({coordinate: c}, target), idx])
          .sort((a, b) => a[0] - b[0])[0][1]]
      : coordinate;
  }

  return Math.sqrt(target.reduce((s, x, idx) => s + Math.pow(x - sightCoordinates[idx], 2), 0));
}

export default function() {
  return {
    namespaced: true,
    state: {
      overview: {
        // type-category: {locale, en: [], zh: [], ....}
      },
      groups: {
        // type-category: {locale, en: [], zh: [], ....}
      },
      entities: {
        // locale, en: []
      },
      entitiesLastLocale: ''
    },
    mutations: {
      SET_OVERVIEW(state, { id, locale, data}) {
        if (state.overview[id] == undefined) {
          state.overview[id] = {};
        }

        state.overview[id][locale] = {
          ...data,
          __updatedAt: Date.now()
        }
      },
      SET_GROUP(state, { id, locale, group, data }) {
        if (state.groups[id] == undefined) {
          state.groups[id] = {};
        }

        if (state.groups[id][locale] == undefined) {
          state.groups[id][locale] = {};
        }

        state.groups[id][locale][group] = {
          ...data,
          __updatedAt: Date.now()
        }
      },
      SET_ENTITY(state, { locale, entity, data }) {
        if (state.entities[locale] == undefined) {
          state.entities[locale] = {};
        }
        
        state.entities[locale][entity] = {
          ...state.entities[locale][entity],
          ...data,
          __updatedAt: Date.now()
        }
      },
      SET_ENTITIES_LAST_LOCALE(state, locale) {
        state.entitiesLastLocale = locale;
      }
    },
    actions: {
      FETCH_OVERVIEW({ state, commit }, { i18n, sightMapType, category }) {
        const { locale } = i18n;
        const now = Date.now();
        const id = `${sightMapType}-${category}`;

        if (state.overview[id]
          && state.overview[id][locale]
          && state.overview[id][locale].groups
          && now - state.overview[id][locale].__updatedAt < cachetime
        ) {
          return ;
        }

        return axios.get(encodeURI(`/api/${sightMapType}/${category}`))
          .then((res) => {
            const data = res.data || [];
            commit('SET_OVERVIEW', { id, locale, data });
          }).catch(err => {
            console.log(err);
            // if (err.response && err.response.status == 404) {
              throw err;
            // }
          });     
      },
      FETCH_GROUP({ state, commit }, { i18n, sightMapType, category, group }) {
        const { locale } = i18n;
        const now = Date.now();
        const id = `${sightMapType}-${category}`;
        if (state.groups[id]
          && state.groups[id][locale] 
          && state.groups[id][locale][group]
          && now - state.groups[id][locale][group].__updatedAt < cachetime
        ) {
          return ;
        }

        return axios.get(encodeURI(`/api/${sightMapType}/${category}/${group}`))
          .then((res) => {
            const data = res.data || {};
            commit('SET_GROUP', { id, locale, group, data });
          }).catch(err => {
            console.log(err);
            // if (err.response && err.response.status == 404) {
              throw err;
            // }
          });
      },
      FETCH_ENTITY({ state, commit, dispatch }, { i18n, sightMapType, category, group, entity, poisToShowChanged, ssr }) {
        const { locale } = i18n;
        const isLocaleSwitch = state.entitiesLastLocale && state.entitiesLastLocale != locale;
        const now = Date.now();
        const entityKey = `${category}/${group}/${entity}`
        if (
          !poisToShowChanged
          && state.entities[locale]
          && state.entities[locale][entityKey]
          && state.entities[locale][entityKey].pois
          && now - state.entities[locale][entityKey].__updatedAt < cachetime
          && !isLocaleSwitch
        ) {
          return ;
        }

        commit('SET_ENTITIES_LAST_LOCALE', locale);

        return axios.get(encodeURI(`/api/${sightMapType}/${category}/${group}/${entity}`))
          .then((res) => {
            const data = res.data || {};
            delete data.pois; // old api still contains pois field, delete to avoid vue reactivity issue
            // data.pois = [];
            // console.log('datapois: ', data.pois)
            const entityCoordinates = data.coordinate || [];
            const coordinate = Array.isArray(entityCoordinates[0]) ? entityCoordinates[0].join('|') : entityCoordinates.join('|');

            commit('SET_ENTITY', {
              locale,
              entity: entityKey,
              data
            });

            // ssr no FETCH_POI
            if (ssr) {
              return;
            } else if (coordinate) {
              dispatch('poi/FETCH_POI', { coordinate, locale })
                .then((pois)=>{
                  data.pois = pois.filter(poi => poi.id !== data.id).map(poi => ({
                    ...poi,
                    distance: distanceFromSightToPoi(data, poi),
                  }));

                  const results = data.pois;
                  // adjust coordinate for same location pois
                  sameCoordGrouper(results).forEach(sameCoordGroup => {
                    sameCoordGroup.forEach((idx, i, arr) => {
                      if (results[idx]) {
                        if (Array.isArray(results[idx].coordinate[0])) {
                          results[idx].coordinate = results[idx].coordinate.map(coordinate => recalculateCoord(coordinate, i, arr.length));
                        } else {
                          results[idx].coordinate = recalculateCoord(results[idx].coordinate, i, arr.length);
                        }
                      }
                    });
                  });

                  commit('SET_ENTITY', {
                    locale,
                    entity: entityKey,
                    data
                  });
                }).catch(err => {
                  console.log(err);
                  // if (err.response && err.response.status == 404) {
                  throw err;
                  // }
                });
            } else {
              console.warn(entityKey, 'missing coordinate data')
            }
          }).catch(err => {
            console.log(err);
            // if (err.response && err.response.status == 404) {
              throw err;
            // }
          });
      },
      FETCH_ENTITY_BY_ID({ state, commit, dispatch }, { i18n, entity, ssr }) {
        const { locale } = i18n;
        const now = Date.now();
        if (state.entities[locale]) {
          const matchKey = 
            Object.keys(state.entities[locale])
              .find(k => k.endsWith(`/${entity}`))
          
          if (matchKey 
            && now - state.entities[locale][matchKey].__updatedAt < cachetime
          ) {
            return 
          }
        }
        
        return axios.get(encodeURI(`/api/sight/${entity}`))
          .then((res) => {
            const data = res.data || {};
            delete data.pois; // old api still contains pois field, delete to avoid vue reactivity issue
            // data.pois = [];
            // console.log('datapois: ', data.pois)
            const group = (data.groups && data.groups[0]) 
              || (data.tours && data.tours[0]) 
              || {}
            const entityKey = `${group.category}/${group.group}/${entity}`
            const entityCoordinates = data.coordinate || [];
            const coordinate = Array.isArray(entityCoordinates[0]) ? entityCoordinates[0].join('|') : entityCoordinates.join('|');

            commit('SET_ENTITY', {
              locale,
              entity: entityKey,
              data
            });

            // ssr no FETCH_POI
            if (ssr) {
              return;
            } else if (coordinate) {
              dispatch('poi/FETCH_POI', { coordinate, locale })
                .then((pois)=>{
                  data.pois = pois.filter(poi => poi.id !== data.id).map((poi) => {
                    return Object.assign(poi, {
                      distance: distanceFromSightToPoi(data, poi),
                    });
                  });

                  commit('SET_ENTITY', { 
                    locale, 
                    entity: entityKey, 
                    data 
                  });
                }).catch(err => {
                  console.log(err);
                  // if (err.response && err.response.status == 404) {
                  throw err;
                  // }
                });
            } else {
              console.warn(entityKey, 'missing coordinate data')
            }
          }).catch(err => {
            console.log(err);
            // if (err.response && err.response.status == 404) {
              throw err;
            // }
          });
      }
    },
    getters: {
      getOverview: (state) => (id, locale) => {
        let raw = state.overview[id] || {};
        raw = raw[locale] || {groups: []};
        return raw;
      },
      getGroup: (state, getters, rootState) => (id, locale, group) => {
        let raw = state.groups[id] || {};
        raw = raw[locale] || {};
        raw = raw[group] ? { 
          ...raw[group],
          sights: Array.isArray(raw[group].sights) ? [...raw[group].sights]: [],
        } : {};

        const { isGeolocationOn, userLocation } = rootState.geolocation;
        if (raw && raw.sights && isGeolocationOn) {
          // sort raw
          raw.sights.sort((a, b) => getSightDistance(a, userLocation) - getSightDistance(b, userLocation));
        }
        
        return raw;
      },
      getEntity: (state) => (locale, category, group, entity) => {
        const raw = state.entities[locale] || {};
        return {
          ...raw[`${category}/${group}/${entity}`]
        }
      },
      getEntityById: (state) => (locale, entity) => {
        const raw = state.entities[locale] || {};
        const matchKey = 
            Object.keys(state.entities[locale])
              .find(k => k.endsWith(`/${entity}`))
        return {
          ...raw[matchKey]
        }
      }
    },
    modules: {
      poi: poi(),
    }
  }
}